/**
 * Items as a tree: an item's links (its ref fields) as groups, the items they link to inside; and the items linking
 * to it, a group per entity and field. Items already on the way down are left out, so a branch never loops back.
 */
import { useState } from "preact/hooks";
import type { ObjEntity, PageItemRow } from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";
import { ATTACHMENTS } from "./attach.ts";
import { linkId } from "./links.ts";

type Row = PageItemRow["data"][number];

/**
 * A group: a ref field of the item (`out`, the items it links to), or the items of an entity (none: untyped) linking
 * to it through a field (`in`; no field for the items it's attached to); how many, not counting the ones on the way
 * down.
 */
export type Group =
  | { dir: "out"; id: string; key: string; label: string; count: number }
  | { dir: "thread"; id: string; thread: string; label: string; count: number }
  | { dir: "in"; id: string; key: string; entity: string | null; label: string; field: string; count: number };

/**
 * An item's groups: its ref fields that link somewhere, in its entity's order (others after, attachments last),
 * then the items linking to it (most first). `path` holds the items above it, which no group counts.
 */
export function groupsOf(row: Row, entities: readonly ObjEntity[], path: readonly Row[] = []): Group[] {
  const above = new Set(path.map((r) => r.id));
  const entity = entities.find((e) => e.id === row.entity);
  const order = (entity?.fields ?? []).map((f) => f.key);
  const rank = (k: string) => {
    if (k === ATTACHMENTS) {
      return Number.MAX_SAFE_INTEGER;
    }
    const i = order.indexOf(k);
    return i < 0 ? order.length : i;
  };
  const out: Group[] = Object.entries(row.refs ?? {})
    .sort(([a], [b]) => rank(a) - rank(b))
    .map(([key, links]) => {
      const field = entity?.fields?.find((f) => f.key === key);
      const label = key === ATTACHMENTS ? t("items.attachments") : field?.label || key;
      const count = links.filter((l) => !above.has(linkId(l))).length;
      return { dir: "out", id: `out:${key}`, key, label, count };
    });
  const into: Group[] = (row.backrefs ?? []).map((g) => {
    const source = entities.find((e) => e.id === g.entity);
    const attached = g.key === ATTACHMENTS;
    // The items above linking here this way (the one just above, say) are already open.
    const seen = path.filter(
      (r) => (r.entity ?? null) === (g.entity ?? null) && (r.refs?.[g.key] ?? []).some((l) => linkId(l) === row.id),
    ).length;
    return {
      dir: "in",
      id: `in:${g.entity ?? ""}:${g.key}`,
      key: g.key,
      entity: g.entity ?? null,
      // The items it's attached to (an email) read as that, whatever their entity.
      label: attached ? t("items.tree.attached") : (source?.name ?? t("items.noEntity")),
      field: attached ? "" : source?.fields?.find((f) => f.key === g.key)?.label || g.key,
      count: g.count - seen,
    };
  });
  // An email's conversation: the thread's other emails (not counting the ones above).
  const thread: Group[] =
    row.thread && (row.thread_count ?? 0) > 1
      ? [
          {
            dir: "thread",
            id: "thread",
            thread: row.thread,
            label: t("items.tree.thread"),
            count: (row.thread_count ?? 0) - 1 - path.filter((r) => r.thread === row.thread).length,
          },
        ]
      : [];
  return [...thread, ...out, ...into].filter((f) => f.count > 0);
}

/** Which of an item's groups are open, and the item itself. */
export function useBranch() {
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState<ReadonlySet<string>>(new Set());
  return {
    open,
    groups,
    toggle: () => setOpen(!open),
    toggleGroup: (id: string) => {
      const next = new Set(groups);
      if (!next.delete(id)) {
        next.add(id);
      }
      setGroups(next);
    },
    /** Opens the item at one of its ref fields (a count clicked in its row). */
    openAt: (key: string) => {
      setOpen(true);
      setGroups(new Set([...groups, `out:${key}`]));
    },
  };
}

export type Branch = ReturnType<typeof useBranch>;

/**
 * The items list with a group's items in it (the entity they are, filtered by the link): where a group's items can
 * be seen as cards or a board, searched and filtered further. None for a thread (its conversation is elsewhere).
 */
export function groupListQuery(rowId: string, group: Group, target: string | null | undefined): string | null {
  const q = new URLSearchParams();
  if (group.dir === "thread") {
    // Its emails as they were sent.
    q.set("thread", group.thread);
    q.set("sort", "data.date");
    return `/items?${q.toString()}`;
  }
  if (group.dir === "in") {
    q.set("entity", group.entity ?? "none");
    q.set("refers_to", `${group.key}:${rowId}`);
  } else {
    if (target) {
      q.set("entity", target);
    }
    q.set("linked_from", `${group.key}:${rowId}`);
  }
  return `/items?${q.toString()}`;
}
